import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SIGNS } from '../data/signs';

export function SignPage() {
  const { signo } = useParams<{ signo: string }>();
  const sign = SIGNS.find(s => s.slug === signo);

  if (!sign) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ color: 'var(--text-muted)' }}>Signo não encontrado.</p>
          <Link to="/" style={{ color: 'var(--petrol)', marginTop: '1rem', display: 'inline-block' }}>← Voltar ao início</Link>
        </div>
      </div>
    );
  }

  const compatibleSigns = sign.compatible.map(slug => SIGNS.find(s => s.slug === slug)).filter(Boolean);
  const ogImage = `https://professya.com/og/signo-${sign.slug}.png`;

  return (
    <>
      <Helmet>
        <title>{sign.name}: Características, Personalidade e Compatibilidade, Professya</title>
        <meta name="description" content={sign.metaDescription} />
        <meta property="og:title" content={`${sign.name}: Características, Personalidade e Compatibilidade`} />
        <meta property="og:description" content={sign.metaDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={`https://professya.com/signos/${sign.slug}`} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${sign.name}: Características e Compatibilidade`} />
        <meta name="twitter:description" content={sign.metaDescription} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="canonical" href={`https://professya.com/signos/${sign.slug}`} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: `${sign.name}: Características, Personalidade e Compatibilidade`,
          description: sign.metaDescription,
          publisher: { '@type': 'Organization', name: 'Professya', url: 'https://professya.com' },
          url: `https://professya.com/signos/${sign.slug}`,
        })}</script>
      </Helmet>

      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-base)', color: 'var(--text-primary)' }}>
        {/* Hero */}
        <div style={{ background: 'linear-gradient(180deg, rgba(245,200,66,0.08) 0%, transparent 100%)', padding: '3rem 1.5rem 2rem', textAlign: 'center' }}>
          <Link to="/" style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', textDecoration: 'none', display: 'inline-block', marginBottom: '1.5rem' }}>
            ✦ Professya
          </Link>
          <div style={{ fontSize: '4rem', marginBottom: '0.75rem' }}>{sign.emoji}</div>
          <h1 className="font-display" style={{ fontSize: 'clamp(2rem,6vw,3rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
            {sign.name}
          </h1>
          <p style={{ color: 'var(--petrol)', fontWeight: 600, fontSize: 'var(--text-label)', marginBottom: '0.5rem' }}>
            {sign.dates}
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            {[['Elemento', sign.element], ['Modalidade', sign.modality], ['Regente', sign.rulingPlanet]].map(([k, v]) => (
              <span key={k} style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', backgroundColor: 'var(--bg-card)', padding: '0.25rem 0.75rem', borderRadius: 'var(--radius-full)', border: '1px solid var(--border-subtle)' }}>
                {k}: <strong style={{ color: 'var(--text-secondary)' }}>{v}</strong>
              </span>
            ))}
          </div>
        </div>

        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '0 1.5rem 4rem' }}>
          {/* Description */}
          <section style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '0.875rem', color: 'var(--petrol)' }}>
              Sobre {sign.name}
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.85, fontSize: 'var(--text-body)' }}>{sign.description}</p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '0.875rem' }}>
              Personalidade de {sign.name}
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.85 }}>{sign.personality}</p>
          </section>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
            <section className="card">
              <h2 className="font-display" style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>💘 Amor e Relacionamentos</h2>
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, fontSize: 'var(--text-label)' }}>{sign.love}</p>
            </section>
            <section className="card">
              <h2 className="font-display" style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>💼 Carreira</h2>
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, fontSize: 'var(--text-label)' }}>{sign.career}</p>
            </section>
          </div>

          {/* Compatibility */}
          <section style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>
              Signos mais compatíveis com {sign.name}
            </h2>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {compatibleSigns.map(cs => cs && (
                <Link key={cs.slug} to={`/compatibilidade/${sign.slug}-${cs.slug}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '0.75rem 1rem', textDecoration: 'none', color: 'var(--text-primary)', transition: 'border-color 0.2s' }}>
                  <span style={{ fontSize: '1.5rem' }}>{cs.emoji}</span>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 'var(--text-label)', margin: 0 }}>{cs.name}</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>Ver compatibilidade →</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* CTAs */}
          <div className="card" style={{ textAlign: 'center', background: 'linear-gradient(135deg, rgba(245,200,66,0.06) 0%, transparent 100%)', border: '1px solid rgba(245,200,66,0.2)' }}>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.125rem', marginBottom: '0.5rem' }}>
              Quer sua previsão personalizada de {sign.name}?
            </p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1.25rem' }}>
              Crie sua conta gratuitamente e receba previsões baseadas no seu mapa natal completo, não só no seu signo solar.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <Link to="/register" className="btn btn-primary">✦ Criar conta grátis</Link>
              <Link to={`/previsao-2027/${sign.slug}`} className="btn btn-secondary">
                Ver previsão 2027 para {sign.name} →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
